import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Info as InfoIcon,
  CheckCircleOutline,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { httpClient } from 'src/libs';

interface ApiKey {
  id: string;
  key: string;  // This is already masked from the API
  scope: string;
  label?: string | null;
  created_at: string;
  last_used_at?: string | null;
  updated_at?: string;
}

interface NewApiKeyResponse {
  success: boolean;
  id: string;
  api_key: string;
  scope: string;
  label?: string | null;
}

const ApiKeysForm: React.FC = () => {
  const theme = useTheme();
  
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [showKeys, setShowKeys] = useState<{ [key: string]: boolean }>({});
  const [openDialog, setOpenDialog] = useState(false);
  const [newKeyScope, setNewKeyScope] = useState('account_admin');
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<{ id: string; key: string } | null>(null);
  const [showNewKeyDialog, setShowNewKeyDialog] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ open: boolean; key: ApiKey | null }>({
    open: false,
    key: null
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    const fetchApiKeys = async () => {
      // Only show loading on initial mount when no data exists
      if (apiKeys.length === 0) {
        setLoading(true);
      }
      
      try {
        const url = `${process.env.REACT_APP_BASE_URL}/comms-api/api_keys`;
        // Force fresh fetch by setting cache headers to bypass 304 responses
        const response = await httpClient.get(url, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (isMounted && response.data) {
          // Check for the actual structure of the response
          if (response.data.success && response.data.api_keys) {
            setApiKeys(response.data.api_keys);
          } else if (Array.isArray(response.data)) {
            setApiKeys(response.data);
          } else {
            setApiKeys([]);
          }
        }
      } catch (error: any) {
        console.error('Failed to fetch API keys:', error);
        if (isMounted) {
          showSnackbar(
            error.response?.status === 401 
              ? 'Unauthorized. Please login again.' 
              : 'Failed to load API keys', 
            'error'
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchApiKeys();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const handleCreateKey = async () => {
    setCreating(true);
    
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/api_keys`;
      const response = await httpClient.post<NewApiKeyResponse>(
        url,
        { 
          scope: newKeyScope,
          label: newKeyLabel || undefined
        }
      );
      
      const newKey = response.data;
      
      // Store the full key for display in success dialog
      setNewlyCreatedKey({ id: newKey.id, key: newKey.api_key });
      
      const maskedKey: ApiKey = {
        id: newKey.id,
        key: maskKey(newKey.api_key),  // Mask it for display in the table
        scope: newKey.scope,
        label: newKey.label,
        created_at: new Date().toISOString()
      };
      
      setApiKeys(prev => [...prev, maskedKey]);
      setNewKeyLabel('');
      setOpenDialog(false);
      setShowNewKeyDialog(true);
      
    } catch (error: any) {
      console.error('Failed to create API key:', error);
      showSnackbar(
        error.response?.status === 403 
          ? 'You do not have permission to create API keys' 
          : 'Failed to create API key', 
        'error'
      );
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKey = (apiKey: ApiKey) => {
    setDeleteConfirmDialog({ open: true, key: apiKey });
  };

  const handleConfirmDelete = async () => {
    const apiKey = deleteConfirmDialog.key;
    if (!apiKey) return;

    setDeleting(apiKey.id);
    setDeleteConfirmDialog({ open: false, key: null });
    
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/api_keys/${apiKey.id}`;
      await httpClient.delete(url);
      
      setApiKeys(prev => prev.filter(key => key.id !== apiKey.id));
      showSnackbar('API key deleted successfully', 'success');
      
      if (newlyCreatedKey && newlyCreatedKey.id === apiKey.id) {
        setNewlyCreatedKey(null);
      }
      
    } catch (error: any) {
      console.error('Failed to delete API key:', error);
      showSnackbar(
        error.response?.status === 404 
          ? 'API key not found' 
          : error.response?.status === 403 
            ? 'You do not have permission to delete this API key'
            : 'Failed to delete API key', 
        'error'
      );
    } finally {
      setDeleting(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmDialog({ open: false, key: null });
  };

  const handleToggleKeyVisibility = (id: string) => {
    setShowKeys(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCopyKey = (key: string, id?: string) => {
    navigator.clipboard.writeText(key);
    if (id) {
      setCopiedKey(id);
      setTimeout(() => setCopiedKey(null), 2000);
    }
    showSnackbar('API key copied to clipboard', 'success');
  };
  
  const handleCloseNewKeyDialog = () => {
    setShowNewKeyDialog(false);
    setNewlyCreatedKey(null);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const maskKey = (key: string) => {
    if (!key) return '••••••••••••••••••••';
    // Show first 7 and last 5 characters, mask the middle
    const prefix = key.substring(0, 7);
    const suffix = key.substring(key.length - 5);
    const maskedLength = Math.max(8, key.length - 12); // At least 8 dots
    return prefix + '•'.repeat(maskedLength) + suffix;
  };

  const getDisplayKey = (apiKey: ApiKey) => {
    // Use the key directly from API - it's already masked
    return apiKey.key || '••••••••••••••••••••';
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" color="text.primary">
          API Keys
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          disabled={loading}
        >
          Create New Key
        </Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <InfoIcon color="info" sx={{ mt: 0.5 }} />
            <Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                API keys allow you to authenticate requests to the Oppy API. Each key has full access to your account's data.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Important:</strong> Keep your API keys secure and never share them in public repositories or client-side code. 
                Newly created keys are shown in full only once.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading && apiKeys.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : apiKeys.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary" paragraph>
                No API keys yet. Create your first key to get started.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setOpenDialog(true)}
              >
                Create Your First Key
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Label</TableCell>
                    <TableCell>Key</TableCell>
                    <TableCell>Scope</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiKeys.map((apiKey) => {
                    const displayKey = getDisplayKey(apiKey);
                    
                    return (
                      <TableRow key={apiKey.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {apiKey.label || 'No Label'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ 
                              fontFamily: 'monospace', 
                              fontSize: '0.875rem'
                            }}
                          >
                            {displayKey}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={apiKey.scope || 'account_admin'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(apiKey.last_used_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(apiKey.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Delete Key">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteKey(apiKey)}
                              color="error"
                              disabled={deleting === apiKey.id}
                            >
                              {deleting === apiKey.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={() => !creating && setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New API Key</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The full API key will be shown only once after creation. Make sure to copy and store it securely.
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            label="Label (Optional)"
            fullWidth
            variant="outlined"
            value={newKeyLabel}
            onChange={(e) => setNewKeyLabel(e.target.value)}
            helperText="Give your key a descriptive name (e.g., 'Production Server', 'Development')"
            disabled={creating}
          />
          <TextField
            margin="dense"
            label="Scope"
            fullWidth
            variant="outlined"
            value={newKeyScope}
            onChange={(e) => setNewKeyScope(e.target.value)}
            helperText="API key scope (default: account_admin)"
            disabled={creating}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateKey}
            variant="contained"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : null}
          >
            {creating ? 'Creating...' : 'Create Key'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={deleteConfirmDialog.open} 
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            <Typography variant="h6">Delete API Key</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              ⚠️ This action cannot be undone!
            </Typography>
            <Typography variant="body2">
              Once deleted, this API key cannot be recovered or regenerated. 
              Any applications or services using this key will immediately lose access.
            </Typography>
          </Alert>
          
          {deleteConfirmDialog.key && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You are about to delete the following API key:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'action.hover' }}>
                {deleteConfirmDialog.key.label && (
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {deleteConfirmDialog.key.label}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                  {deleteConfirmDialog.key.key || '••••••••••••••••••••'}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  Scope: {deleteConfirmDialog.key.scope || 'account_admin'}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  Created: {formatDate(deleteConfirmDialog.key.created_at)}
                </Typography>
              </Paper>
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Before deleting:</strong>
                </Typography>
                <Typography variant="body2" component="ul" sx={{ mb: 0, pl: 2 }}>
                  <li>Make sure no active applications are using this key</li>
                  <li>Update any scripts or services that depend on it</li>
                  <li>Consider creating a replacement key first if needed</li>
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting === deleteConfirmDialog.key?.id}
            startIcon={deleting === deleteConfirmDialog.key?.id ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting === deleteConfirmDialog.key?.id ? 'Deleting...' : 'Delete API Key'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={showNewKeyDialog} 
        onClose={handleCloseNewKeyDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutline color="success" />
            <Typography variant="h6">API Key Created Successfully</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              ⚠️ Important: Copy this API key now!
            </Typography>
            <Typography variant="body2">
              This is the only time you'll be able to see the full API key. 
              For security reasons, we won't show it again. Please copy it and store it securely.
            </Typography>
          </Alert>
          
          {newlyCreatedKey && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Your new API key:
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  backgroundColor: 'background.default',
                  border: '2px solid',
                  borderColor: 'success.main'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{ 
                        fontFamily: 'monospace',
                        fontSize: '16px',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {maskKey(newlyCreatedKey.key)}
                    </Typography>
                  </Box>
                  <Tooltip title={copiedKey === 'new' ? "Copied full key!" : "Copy full API key"}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        handleCopyKey(newlyCreatedKey.key);
                        setCopiedKey('new');
                        setTimeout(() => setCopiedKey(null), 2000);
                      }}
                      color={copiedKey === 'new' ? "success" : "primary"}
                      size="large"
                      startIcon={<CopyIcon />}
                    >
                      {copiedKey === 'new' ? 'Copied!' : 'Copy Key'}
                    </Button>
                  </Tooltip>
                </Box>
              </Paper>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>How to use this API key:</strong>
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                  Include it in your API requests as a Bearer token:
                  <Box component="code" sx={{ 
                    display: 'block', 
                    mt: 1, 
                    p: 1, 
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '12px'
                  }}>
                    Authorization: Bearer {newlyCreatedKey.key.substring(0, 10)}...
                  </Box>
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            variant="contained" 
            onClick={handleCloseNewKeyDialog}
          >
            I've Copied the Key
          </Button>
          <Button 
            variant="outlined" 
            onClick={handleCloseNewKeyDialog}
            color="warning"
          >
            Close Without Copying
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApiKeysForm;